import { api } from "@/lib/api-client"

/**
 * Delegation mode for a capability in the per-customer delegation matrix
 * (see `core/admin_capabilities.py`). `hidden` and `available` never grant
 * real access — only `manage` does; `available` is a locked/upsell preview
 * state shown to customer admins for capabilities they don't have yet.
 */
export type CapabilityMode = "hidden" | "available" | "manage"

export type DelegationRegistryEntry = {
  key: string
  tier: "delegable" | "platform_only"
  description: string
  mode: CapabilityMode
}

export type DelegationResponse = {
  registry: DelegationRegistryEntry[]
  capabilities: Record<string, CapabilityMode>
}

/** Platform-only. Full capability registry (keys, tiers, descriptions) plus the current matrix. */
export async function getDelegationMatrix(): Promise<DelegationResponse> {
  const response = await api.get("/admin/delegation")
  return response.data || response
}

/**
 * Platform-only. Merges the given capabilities into the stored delegation
 * matrix (server-side, against the freshest state) — capabilities omitted
 * from `capabilities` are left untouched. `hidden` removes a key entirely.
 */
export async function updateDelegationMatrix(
  capabilities: Record<string, CapabilityMode>
): Promise<{ success: boolean; capabilities: Record<string, CapabilityMode> }> {
  const response = await api.put("/admin/delegation", { capabilities })
  return response.data || response
}
